import { ChartType } from "chart.js";
import { BaseSeries, Series } from "./series";

function createValueGetter<T>(propertyName: keyof T): (v: T, i: number) => number
{
    return (v, i) => v[propertyName] as number;
}

export class SeriesConfig<T = unknown, TChartType extends ChartType = ChartType>
    implements BaseSeries<T>
{
    readonly name: string;
    readonly type?: ChartType | undefined;

    xAxisId?: string;
    yAxisId?: string;

    readonly getValue: (v: T, i: number) => number;


    private _getLabelFn?: (v: T, i: number) => string;
    private _values?: ReadonlyArray<number>;
    private _originalLabels?: ReadonlyArray<string>;
    private _labels?: ReadonlyArray<string>;

    constructor(
        readonly original: Readonly<Series<T, TChartType>>,
        readonly index: number,
        private readonly source: ReadonlyArray<T>)
    {
        this.name = original.name;
        this.type = original.type;

        this.xAxisId = original.xAxisId;
        this.yAxisId = original.yAxisId;

        const getValue = original.getValue as BaseSeries["getValue"];
        this.getValue = typeof getValue === "function"
            ? getValue!
            : createValueGetter<T>(getValue!);

        this._getLabelFn = original.labels && typeof original.labels === "function"
            ? original.labels as (v: T, i: number) => string
            : undefined;
        this._originalLabels = original.labels && Array.isArray(original.labels)
            ? original.labels as ReadonlyArray<string>
            : undefined;
    }

    get values(): ReadonlyArray<number>
    {
        return this._values
            ?? (this._values = this.source.map((x, i) => this.getValue(x, i)));
    }

    get labels(): ReadonlyArray<string> | undefined
    {
        if (!this._labels)
        {
            this._labels = this._getLabelFn
                ? this.source.map((x, i) => this._getLabelFn!(x, i))
                : this._originalLabels;
        }

        return this._labels;
    }

    get<K extends keyof Series<T, TChartType>>(field: K): Series<T, TChartType>[typeof field]
    {
        return this.original[field];
    }
}
